import {
  enrichHonestScanMetrics,
  parseHonestScanMetrics,
  walkTracksFoundFromProgress,
} from './scanProgressTruth';

/** Connector silence before the web finalizes the scan UI on its own (not from scan start). */
export const SCAN_PROGRESS_WATCHDOG_SILENCE_MS = 20_000;

let watchdogTimer = null;
let watchdogOpId = null;
let watchdogOnRescue = null;
let watchdogCompleted = false;
let lastProgressAt = 0;
let lastPhase = '';
let lastProgressCount = null;
let lastTotalCount = null;

export function extractScanProgressCounts(d, opId) {
  const metrics = enrichHonestScanMetrics(opId, d, parseHonestScanMetrics(d));
  const walkRunningTotal = metrics.walkRunningTotal;
  const walkCount = walkTracksFoundFromProgress(d);
  const progress = metrics.terminalFiles ?? walkRunningTotal ?? walkCount ?? (
    typeof d.current_file === 'number' ? d.current_file : null
  );
  const total = metrics.walkFilesFound ?? (
    typeof d.total_files === 'number' && d.total_files >= 0 ? d.total_files : null
  );
  return { progress, total };
}

export function buildWatchdogRescueLabel(progress, total) {
  if (total != null && total > 0 && progress != null && progress < total) {
    return `Scan finished — some files did not complete (${Number(progress).toLocaleString()} of ${Number(total).toLocaleString()})`;
  }
  return 'Scan finished — connector stopped reporting progress';
}

export function resetScanProgressWatchdog() {
  if (watchdogTimer) {
    clearTimeout(watchdogTimer);
    watchdogTimer = null;
  }
  watchdogOpId = null;
  watchdogOnRescue = null;
  watchdogCompleted = false;
  lastProgressAt = 0;
  lastPhase = '';
  lastProgressCount = null;
  lastTotalCount = null;
}

export function stopScanProgressWatchdog() {
  if (watchdogTimer) {
    clearTimeout(watchdogTimer);
    watchdogTimer = null;
  }
  watchdogOpId = null;
  watchdogOnRescue = null;
}

export function markScanProgressWatchdogComplete() {
  watchdogCompleted = true;
  stopScanProgressWatchdog();
}

function scheduleWatchdogCheck() {
  if (watchdogTimer) {
    clearTimeout(watchdogTimer);
    watchdogTimer = null;
  }
  if (watchdogCompleted || !watchdogOpId || !watchdogOnRescue) return;
  const silentMs = Date.now() - lastProgressAt;
  const remaining = SCAN_PROGRESS_WATCHDOG_SILENCE_MS - silentMs;
  if (remaining <= 0) {
    watchdogCompleted = true;
    const snapshot = {
      opId: watchdogOpId,
      phase: lastPhase,
      progress: lastProgressCount,
      total: lastTotalCount,
      silentMs,
    };
    const onRescue = watchdogOnRescue;
    stopScanProgressWatchdog();
    onRescue?.(snapshot);
    return;
  }
  watchdogTimer = setTimeout(scheduleWatchdogCheck, Math.min(remaining, 1000));
}

/** Start silence watchdog — fires only when no progress event for SCAN_PROGRESS_WATCHDOG_SILENCE_MS. */
export function startScanProgressWatchdog(opId, onRescue) {
  resetScanProgressWatchdog();
  watchdogOpId = opId;
  watchdogOnRescue = onRescue;
  lastProgressAt = Date.now();
  scheduleWatchdogCheck();
}

/** Reset silence clock on each connector scan-progress event while scan is active. */
export function noteScanProgressWatchdog(d, opId) {
  if (watchdogCompleted) return;
  if (!watchdogOpId) return;
  if (opId && opId !== watchdogOpId) return;
  const phase = String(d?.phase ?? '');
  if (phase === 'scan_complete' && d?.terminal === true) return;
  if (phase === 'cancelled' || phase === 'error') return;
  const counts = extractScanProgressCounts(d, opId ?? watchdogOpId);
  lastProgressAt = Date.now();
  lastPhase = phase;
  lastProgressCount = counts.progress;
  lastTotalCount = counts.total;
  scheduleWatchdogCheck();
}

export function logScanCompletedViaConnector() {
  console.info('[scan] completed via connector scan_complete');
}

export function logScanWatchdogRescue(snapshot) {
  const progress = snapshot.progress ?? '?';
  const total = snapshot.total ?? '?';
  console.warn(
    `[scan-watchdog] connector silent for ${Math.round(snapshot.silentMs / 1000)}s — finalizing UI without scan_complete; lastPhase=${snapshot.phase} progress=${progress}/${total}`,
  );
}
